import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { 
  NOTIFICATION_TYPES, 
  NOTIFICATION_TYPE_LABELS,
  RESPONSE_TYPES
} from '../../constants/notificationConstants';
import { dateUtils } from '../../utils/helpers';
import NotificationResponse from './NotificationResponse';

const NotificationCard = ({ notification, onClick }) => {
  const { isMobile, getResponsiveValue } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  const [localNotification, setLocalNotification] = useState(notification);

  const cardPadding = getResponsiveValue({
    mobile: 'p-4',
    desktop: 'p-6'
  });

  const handleCardClick = (e) => {
    // Don't expand/collapse if clicking on response buttons
    if (e.target.closest('button')) {
      return;
    }
    
    setIsExpanded(!isExpanded);
    if (onClick) {
      onClick(localNotification);
    }
  };

  // Handle response updates from the NotificationResponse component
  const handleResponseUpdate = (notificationId, response) => {
    console.log('NotificationCard: Response updated', notificationId, response);
    
    // Update local notification state immediately
    setLocalNotification(prev => ({
      ...prev,
      userResponse: {
        response,
        respondedAt: new Date()
      }
    }));
  };

  const getTypeIcon = (type) => {
    const icons = {
      [NOTIFICATION_TYPES.GENERAL]: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.959 8.959 0 01-2.4-.32l-3.2 2.88A1 1 0 015 21V9a8 8 0 018-8c4.418 0 8 3.582 8 8z" />
        </svg>
      ),
      [NOTIFICATION_TYPES.INVITATION]: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      [NOTIFICATION_TYPES.MEETING]: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.196-2.121M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.196-2.121M7 20v-2c0-.656.126-1.283.356-1.857M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      )
    };
    return icons[type] || icons[NOTIFICATION_TYPES.GENERAL];
  };

  const getTypeColor = (type) => {
    const colors = {
      [NOTIFICATION_TYPES.GENERAL]: 'text-accent bg-accent-subtle',
      [NOTIFICATION_TYPES.INVITATION]: 'text-success bg-success-subtle',
      [NOTIFICATION_TYPES.MEETING]: 'text-warning bg-warning-subtle'
    };
    return colors[type] || colors[NOTIFICATION_TYPES.GENERAL];
  };

  const formatEventDateTime = () => {
    if (!localNotification.eventDate) return null;
    
    const date = dateUtils.formatDate(localNotification.eventDate);
    const time = localNotification.eventTime || '';
    
    return `${date}${time ? ` at ${time}` : ''}`;
  };

  const needsResponse = localNotification.type !== NOTIFICATION_TYPES.GENERAL && 
                      localNotification.userResponse?.response === RESPONSE_TYPES.PENDING;

  return (
    <div 
      className={`
        bg-surface-elevated rounded-xl border transition-all duration-200 cursor-pointer
        ${!localNotification.isRead 
          ? 'border-notification shadow-theme-md bg-notification-subtle' 
          : 'border-border-primary shadow-theme-sm hover:shadow-theme-md'
        }
        ${isExpanded ? 'ring-2 ring-accent ring-opacity-20' : ''}
      `}
      onClick={handleCardClick}
    >
      <div className={cardPadding}>
        
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-3">
            {/* Type Icon */}
            <div className={`p-2 rounded-lg ${getTypeColor(localNotification.type)}`}>
              {getTypeIcon(localNotification.type)}
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1">
                <h3 className="font-semibold text-text-primary truncate">
                  {localNotification.type === NOTIFICATION_TYPES.MEETING 
                    ? localNotification.meetingHeading 
                    : NOTIFICATION_TYPE_LABELS[localNotification.type]
                  }
                </h3>
                {!localNotification.isRead && (
                  <div className="w-2 h-2 bg-notification-badge rounded-full flex-shrink-0"></div>
                )}
              </div>
              <p className="text-sm text-text-secondary">
                {dateUtils.formatDateTime(localNotification.sentAt)}
              </p>
            </div>
          </div>

          {/* Expand/Collapse Icon */}
          <button 
            className="p-1 text-text-tertiary hover:text-text-primary"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
          >
            <svg 
              className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        {/* Preview Content */}
        <div className="mb-3">
          <p className="text-text-secondary text-sm line-clamp-2">
            {localNotification.content}
          </p>
        </div>

        {/* Quick Info */}
        {(localNotification.venue || localNotification.eventDate) && (
          <div className="flex flex-wrap gap-4 text-xs text-text-tertiary mb-3">
            {localNotification.venue && (
              <div className="flex items-center space-x-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>{localNotification.venue}</span>
              </div>
            )}
            {localNotification.eventDate && (
              <div className="flex items-center space-x-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{formatEventDateTime()}</span>
              </div>
            )}
          </div>
        )}

        {/* Response Status */}
        {localNotification.userResponse && localNotification.userResponse.response !== RESPONSE_TYPES.PENDING && (
          <div className="mb-3">
            <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
              localNotification.userResponse.response === RESPONSE_TYPES.ACCEPT ? 'bg-success-subtle text-success' :
              localNotification.userResponse.response === RESPONSE_TYPES.REJECT ? 'bg-error-subtle text-error' :
              'bg-warning-subtle text-warning'
            }`}>
              Your response: {localNotification.userResponse.response.charAt(0).toUpperCase() + localNotification.userResponse.response.slice(1)}
            </div>
          </div>
        )}

        {/* Expanded Content */}
        {isExpanded && (
          <div className="mt-4 pt-4 border-t border-border-primary space-y-4">
            
            {/* Full Content */}
            <div>
              <h4 className="font-medium text-text-primary mb-2">Message</h4>
              <p className="text-text-secondary whitespace-pre-wrap">{localNotification.content}</p>
            </div>

            {/* Meeting/Invitation Details */}
            {localNotification.type === NOTIFICATION_TYPES.MEETING && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {localNotification.chairedBy && (
                  <div>
                    <h5 className="font-medium text-text-primary text-sm mb-1">Chaired By</h5>
                    <p className="text-text-secondary text-sm">{localNotification.chairedBy}</p>
                  </div>
                )}
                {localNotification.venue && (
                  <div>
                    <h5 className="font-medium text-text-primary text-sm mb-1">Venue</h5>
                    <p className="text-text-secondary text-sm">{localNotification.venue}</p>
                  </div>
                )}
                {localNotification.eventDate && (
                  <div>
                    <h5 className="font-medium text-text-primary text-sm mb-1">Date & Time</h5>
                    <p className="text-text-secondary text-sm">{formatEventDateTime()}</p>
                  </div>
                )}
                {localNotification.description && (
                  <div className="md:col-span-2">
                    <h5 className="font-medium text-text-primary text-sm mb-1">Description</h5>
                    <p className="text-text-secondary text-sm">{localNotification.description}</p>
                  </div>
                )}
              </div>
            )}

            {localNotification.type === NOTIFICATION_TYPES.INVITATION && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {localNotification.venue && (
                  <div>
                    <h5 className="font-medium text-text-primary text-sm mb-1">Venue</h5>
                    <p className="text-text-secondary text-sm">{localNotification.venue}</p>
                  </div>
                )}
                {localNotification.eventDate && (
                  <div>
                    <h5 className="font-medium text-text-primary text-sm mb-1">Event Date & Time</h5>
                    <p className="text-text-secondary text-sm">{formatEventDateTime()}</p>
                  </div>
                )}
              </div>
            )}

            {/* Attachments */}
            {localNotification.attachments && localNotification.attachments.length > 0 && (
              <div>
                <h5 className="font-medium text-text-primary text-sm mb-2">Attachments</h5>
                <div className="space-y-2">
                  {localNotification.attachments.map((file, index) => (
                    <a
                      key={index}
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-2 p-2 bg-attachment-subtle text-attachment hover:bg-attachment-subtle/80 rounded-lg transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                      <span className="text-sm">{file.name}</span>
                      <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Response Section */}
            {needsResponse && (
              <div className="pt-4 border-t border-border-primary">
                <NotificationResponse
                  notificationId={localNotification.id}
                  currentResponse={localNotification.userResponse?.response}
                  onResponseUpdate={handleResponseUpdate}
                />
              </div>
            )}
          </div>
        )}

        {/* Quick Response Buttons (collapsed view) */}
        {!isExpanded && needsResponse && (
          <div className="mt-3 pt-3 border-t border-border-primary">
            <NotificationResponse
              notificationId={localNotification.id}
              currentResponse={localNotification.userResponse?.response}
              compact={true}
              onResponseUpdate={handleResponseUpdate}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationCard;